import { IModule, ModuleContent, ModuleParams } from '../types';
import { PdfFont, PdfBrush, PdfSize, ETextAlign, PdfRect, PageGen, PdfColor } from '../../pdf/pdf';
import { parseLatex } from '../../pdf/latex/parser';
import { layoutNode } from '../../pdf/latex/layout';
import { LatexBox, LayoutCtx, LayoutCtx as LayoutContext } from '../../pdf/latex/types';
import { PDFFont } from 'pdf-lib';

function createLayoutCtx(page: PageGen, size: number, displayStyle: boolean): LayoutCtx {
  // Get actual PDFFont objects from the page
  const regularFont = page.pdf.getFont(PdfFont.FONT_NAME);
  const italicFont = page.pdf.getFont(PdfFont.ITALIC_FONT_NAME);

  if (!regularFont) {
    throw new Error(`Regular font '${PdfFont.FONT_NAME}' not found in PDF document`);
  }
  if (!italicFont) {
    throw new Error(`Italic font '${PdfFont.ITALIC_FONT_NAME}' not found in PDF document`);
  }

  return {
    regularFont,
    italicFont,
    size,
    level: 0,
    displayStyle,
  };
}

export const latexModule: IModule = {
  name: 'latex',

  parse(source: string, attrs: Record<string, string>): ModuleContent {
    const isBlock =
      attrs.block === 'true' ||
      source.includes('\\frac') ||
      source.includes('\\sum') ||
      source.includes('\\int');
    return {
      type: 'latex',
      attrs: { ...attrs, block: isBlock ? 'true' : 'false' },
      content: source,
    };
  },

  measure(content: ModuleContent, params: ModuleParams): PdfSize {
    try {
      const isBlock = content.attrs?.block === 'true';
      const page = params.page!;
      if (!page) {
        // Without a page, we can't get fonts, so estimate size
        const fontSize = isBlock ? 20 : 12;
        return new PdfSize(content.content.length * fontSize * 0.6, fontSize * 1.5);
      }

      const node = parseLatex(content.content);
      const box = layoutNode(node, createLayoutCtx(page, isBlock ? 20 : 12, isBlock));

      return new PdfSize(box.width, box.height + box.depth);
    } catch (e) {
      console.error('LaTeX measure error:', e);
      return new PdfSize(50, 20);
    }
  },

  draw(content: ModuleContent, rect: PdfRect, align: ETextAlign, params: ModuleParams): void {
    const page = params.page!;
    if (!page) {
      throw new Error('PageGen required for LaTeX drawing');
    }

    try {
      const isBlock = content.attrs?.block === 'true';
      const fontSize = isBlock ? 20 : 12;

      const node = parseLatex(content.content);
      const box = layoutNode(node, createLayoutCtx(page, fontSize, isBlock));

      let x = rect.left;
      // Horizontal alignment
      if (
        align === ETextAlign.Center ||
        align === ETextAlign.TopCenter ||
        align === ETextAlign.BottomCenter
      ) {
        x = rect.left + (rect.width - box.width) / 2;
      } else if (
        align === ETextAlign.TopRight ||
        align === ETextAlign.CenterRight ||
        align === ETextAlign.BottomRight
      ) {
        x = rect.right - box.width;
      }

      let y: number;
      // Vertical alignment
      if (
        align === ETextAlign.Center ||
        align === ETextAlign.CenterLeft ||
        align === ETextAlign.CenterRight
      ) {
        // Center vertically in the rect
        y = rect.top + (rect.height - box.height) / 2 + box.height - box.depth;
      } else if (
        align === ETextAlign.TopLeft ||
        align === ETextAlign.TopCenter ||
        align === ETextAlign.TopRight
      ) {
        // Top align
        y = rect.top + box.height - box.depth;
      } else {
        // Bottom align (default) - BottomLeft, BottomCenter, BottomRight
        y = rect.bottom + box.depth;
      }

      const brush = params.brush || PdfBrush.Black;

      box.draw(page, x, y, brush);
    } catch (e) {
      const font = params.font || new PdfFont(PdfFont.FONT_NAME, 10);
      page.drawString(
        `LaTeX Error: ${(e as Error).message}`,
        font,
        PdfBrush.Red,
        null,
        rect,
        ETextAlign.CenterLeft
      );
    }
  },
};
